import type { ConfiguratorDefinition } from "~/Configurators/common/v2/StepDefinition";
import { RESET_SELECTION, RESET_STANDARD } from "~/Configurators/common/advancedTranslationUtils";
import * as CarriageCstic from "./KWLTCarriageCstics";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { csticsHaveValue, getCsticsValue } from "~/Configurators/common/v2/helper";
import { userIsCustomer } from "~/utils/roles";
import { CsticNames } from "~/Configurators/Carriage/components/ConfigureCarriageSubstep/constants";

export const KWLTCarriagesDefinition: ConfiguratorDefinition = {
  description: [
    {
      condition: ({ mainKmat }) => {
        return mainKmat.cstics[CarriageCstic.MaterialName]?.value === "KWLT";
      },
      value: "configurator.carriage.summary.KWLT.description",
    },
  ],
  mediaUrl: [
    {
      condition: ({ mainKmat }) => {
        const [carriageDesign] = getCsticsValue(mainKmat, CarriageCstic.CarriageDesign);
        return ["A", "AL"].includes(carriageDesign);
      },
      value: "configurator.carriage.summary.KWLT.A_AL.image",
    },
    {
      condition: ({ mainKmat }) => {
        const [carriageDesign] = getCsticsValue(mainKmat, CarriageCstic.CarriageDesign);
        return ["AS"].includes(carriageDesign);
      },
      value: "configurator.carriage.summary.KWLT.AS.image",
    },
    {
      condition: ({ mainKmat }) => {
        const [carriageDesign] = getCsticsValue(mainKmat, CarriageCstic.CarriageDesign);
        // Mismatch between key and value - on purpose
        return ["US", "ESC"].includes(carriageDesign);
      },
      value: "configurator.carriage.summary.KWLT.US.image",
    },
    {
      condition: ({ mainKmat }) => {
        const [carriageDesign] = getCsticsValue(mainKmat, CarriageCstic.CarriageDesign);
        return ["S", "SL", "H", "HL"].includes(carriageDesign);
      },
      value: "configurator.carriage.summary.KWLT.S_SL_H_HL.image",
    },
  ],
  pages: [
    {
      name: "configurator.carriage.page.1.name",
      enabled: true,
      resetLabel: RESET_SELECTION,
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.Series,

          name: "configurator.carriage.step.series.name",
          description: "configurator.carriage.step.series.description",

          tiles: [
            {
              name: "KWLT",
              description: "configurator.carriage.cstic.series.KWLT.description",
              imageUrl: "configurator.carriage.cstic.series.KWLT.image",
              info: "configurator.carriage.cstic.series.KWLT.info",
              cstics: [
                {
                  csticName: CarriageCstic.MaterialName,
                  value: "KWLT",
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [CarriageCstic.MaterialName],

          visible: true,
        },
        {
          type: SubstepType.Size,

          name: "configurator.carriage.step.size.name",
          description: "configurator.carriage.step.size.description",

          tiles: [
            {
              name: "15",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "15",
                },
              ],
            },
            {
              name: "20",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "20",
                },
              ],
            },
            {
              name: "25",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "25",
                },
              ],
            },
            {
              name: "30",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "30",
                },
              ],
            },
            {
              name: "35",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "35",
                },
              ],
            },
            {
              name: "45",
              cstics: [
                {
                  csticName: CarriageCstic.SizeCode,
                  value: "45",
                },
              ],
            },
          ],
          tilesPerRow: 12,

          csticsToReset: [CarriageCstic.SizeCode],

          visible: true,
        },
        {
          type: SubstepType.Coating,

          name: "configurator.carriage.step.coating.name",
          description: "configurator.carriage.step.coating.description",
          info: "configurator.carriage.step.coating.info",
          footerInfo: [
            [
              {
                condition: ({ mainKmat }) => {
                  const [coatingValue] = getCsticsValue(mainKmat, CarriageCstic.Coating);
                  return !["NONE"].includes(coatingValue);
                },
                value: "configurator.carriage.step.coating.footerInfo.preload",
              },
            ],
            [
              {
                condition: ({ mainKmat }) => {
                  const [coatingValue] = getCsticsValue(mainKmat, CarriageCstic.Coating);
                  return !["NONE"].includes(coatingValue);
                },
                value: "configurator.carriage.step.coating.footerInfo.default",
              },
            ],
          ],
          tiles: [
            {
              name: "configurator.carriage.cstic.coating./.name",
              cstics: [
                {
                  csticName: CarriageCstic.Coating,
                  value: "NONE",
                },
              ],
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [CarriageCstic.Coating],

          visible: true,
        },
        {
          type: SubstepType.Preload,

          name: "configurator.carriage.step.preload.name",
          description: "configurator.carriage.step.preload.description",
          info: "configurator.carriage.step.preload.info.KWLT",

          tiles: [
            {
              name: "configurator.carriage.cstic.preload.V0.title",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V0",
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.preload.V1.title",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V1",
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.preload.V2.title",
              cstics: [
                {
                  csticName: CarriageCstic.Preload,
                  value: "V2",
                },
              ],
            },
          ],
          tilesPerRow: 6,

          csticsToReset: [CarriageCstic.Preload],

          visible: true,
        },
        {
          type: SubstepType.Accuracy,

          name: "configurator.carriage.step.accuracy.name",
          description: "configurator.carriage.step.accuracy.description",
          info: "configurator.carriage.step.accuracy.info.KWLT",

          tiles: [
            {
              name: "configurator.carriage.cstic.accuracy.G1.title",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G1",
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.accuracy.G2.title",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G2",
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.accuracy.G3.title",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G3",
                },
              ],
            },
            {
              name: "configurator.carriage.cstic.accuracy.G4.title",
              cstics: [
                {
                  csticName: CarriageCstic.Accuracy,
                  value: "G4",
                },
              ],
            },
          ],
          tilesPerRow: 6,

          footerInfo: [[]],

          csticsToReset: [CarriageCstic.Accuracy],

          visible: true,
        },
        {
          type: SubstepType.Lubrication,
          name: "configurator.carriage.step.lubrication.name",
          description: "configurator.carriage.step.lubrication.description",
          info: "configurator.carriage.step.lubrication.info",
          tiles: [
            {
              name: "configurator.carriage.cstic.lubrication.basic.name",
              description: "configurator.carriage.cstic.lubrication.basic.description",
              cstics: [
                {
                  csticName: CarriageCstic.GreasingPerGreaseGroup,
                  value: "STD",
                },
              ],
              overrideCsticsToEnable: true,
              visible: true,
            },
          ],
          tilesPerRow: 4,
          csticsToReset: [CarriageCstic.GreasingPerGreaseGroup],
          visible: true,
          extraConfig: {
            hasDropdown: false,
          },
        },
      ],
    },
    {
      name: "configurator.carriage.page.2.name",
      enabled: true,
      resetLabel: RESET_STANDARD,
      resetRequiresConfirmation: true,
      substeps: [
        {
          type: SubstepType.CarriageType,

          name: "configurator.carriage.step.carriageType.name",
          description: "configurator.carriage.step.carriageType.description",

          groupedTiles: [
            {
              tiles: [
                {
                  name: "configurator.carriage.cstic.carriageType.A.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.A.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "A",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.carriage.cstic.carriageType.AL.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.AL.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "AL",
                    },
                  ],
                  visible: true,
                },
              ],
              visible: true,
            },

            {
              tiles: [
                {
                  name: "configurator.carriage.cstic.carriageType.S.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.S.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "S",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.carriage.cstic.carriageType.SL.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.SL.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "SL",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.carriage.cstic.carriageType.H.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.H.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "H",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.carriage.cstic.carriageType.HL.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.HL.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "HL",
                    },
                  ],
                  visible: true,
                },
              ],
              visible: true,
            },

            {
              tiles: [
                {
                  name: "configurator.carriage.cstic.carriageType.AS.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.AS.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "AS",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.carriage.cstic.carriageType.US.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.US.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "US",
                    },
                  ],
                  visible: true,
                },
                {
                  name: "configurator.carriage.cstic.carriageType.ESC.name",
                  imageUrl: "configurator.carriage.cstic.carriageType.ESC.image",
                  cstics: [
                    {
                      csticName: CarriageCstic.CarriageDesign,
                      value: "ESC",
                    },
                  ],
                  visible: true,
                },
              ],
              visible: true,
            },
          ],
          tilesPerRow: 4,

          csticsToReset: [CarriageCstic.CarriageDesign],

          visible: true,
        },
        {
          type: SubstepType.LocatingFace,
          name: "configurator.carriage.step.locatingFace.name",
          description: "configurator.carriage.step.locatingFace.description",
          info: "configurator.carriage.step.locatingFace.info",
          footerInfo: ["configurator.carriage.step.locatingFace.footerInfo"],
          tiles: [
            {
              name: "configurator.carriage.cstic.locatingFace.O.name",
              imageUrl: "configurator.carriage.cstic.locatingFace.O.image",
              cstics: [
                {
                  csticName: CarriageCstic.LocatingFace,
                  value: "O",
                },
              ],
            },
            // {
            //   name: "configurator.carriage.cstic.locatingFace.U.name",
            //   imageUrl: "configurator.carriage.cstic.locatingFace.U.image",
            //   cstics: [
            //     {
            //       csticName: CarriageCstic.LocatingFace,
            //       value: "U",
            //     },
            //   ],
            // },
          ],
          tilesPerRow: 4,
          csticsToReset: [CarriageCstic.LocatingFace],
          visible: true,
        },
        {
          type: SubstepType.ConfigureCarriage,
          name: "configurator.carriage.step.configureCarriage.name",
          tiles: [],
          tilesPerRow: 4,
          visible: true,

          extraTexts: {
            sealingAndLubricationSectionName:
              "configurator.carriage.step.configureCarriage.section.sealingAndLubrication.name",
            sealingAndLubricationSectionInfo:
              "configurator.carriage.step.configureCarriage.section.sealingAndLubrication.info",
            lubricationConnectorSectionName: "configurator.carriage.cstic.lubricationConnector.name",
            lubricationConnectorSectionInfo:
              "configurator.carriage.step.configureCarriage.section.lubricationConnector.info",
            lubricationConnectorManualSectionName:
              "configurator.carriage.step.configureCarriage.section.lubricationConnector.manual.name",
            lubricationConnectorCentralSectionName:
              "configurator.carriage.step.configureCarriage.section.lubricationConnector.central.name",
            edit: "configurator.carriage.step.configureCarriage.btn.edit",
          },

          inputCstics: {
            [CsticNames.KITLeft]: {
              name: CarriageCstic.KitsLeft,
              required: true,
              disabled: [
                {
                  condition: ({ mainKmat }) => getCsticsValue(mainKmat, CarriageCstic.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.KITRight]: {
              name: CarriageCstic.KitsRight,
              required: true,
              disabled: true,
            },
            [CsticNames.KITCenter]: {
              name: CarriageCstic.KitsCenter,
              required: true,
              // SCHAEFF-36818 - make kit center always visible as per client request. Changing this again will be a CR.
              // visible: [
              //   {
              //     condition: ({ mainKmat }) => {
              //       return csticHasValue(mainKmat, CarriageCstic.KitsRight);
              //     },
              //     value: true,
              //   },
              //   false,
              // ],
              disabled: [
                {
                  condition: ({ mainKmat }) => getCsticsValue(mainKmat, CarriageCstic.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.LubricationConnectorOrientation]: {
              name: CarriageCstic.LubricationConnectorOrientation,
              required: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    return csticsHaveValue(
                      mainKmat,
                      CarriageCstic.KitsLeft,
                      CarriageCstic.KitsCenter,
                      CarriageCstic.KitsRight,
                    );
                  },
                  value: true,
                },
                false,
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => getCsticsValue(mainKmat, CarriageCstic.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.LubricationConnectorPosition]: {
              name: CarriageCstic.LubricationConnectorPosition,
              // required: true,
              visible: [
                // {
                //   condition: ({ mainKmat }) => {
                //     const [connectorOrientation] = getCsticsValue(
                //       mainKmat,
                //       CarriageCstic.LubricationConnectorOrientation,
                //     );
                //     return ["L", "R"].includes(connectorOrientation);
                //   },
                //   value: true,
                // },
                false,
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => getCsticsValue(mainKmat, CarriageCstic.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.LubricationConnector]: {
              name: CarriageCstic.LubricationConnector,
              required: true,
              visible: [
                {
                  condition: ({ mainKmat }) => {
                    const [lubricationConnectorStatus] = getCsticsValue(
                      mainKmat,
                      CarriageCstic.LubricationConnectorOrientation,
                    );
                    return ["L", "R", "BL", "BR"].includes(lubricationConnectorStatus);
                  },
                  value: true,
                },
                false,
              ],
              disabled: [
                {
                  condition: ({ mainKmat }) => getCsticsValue(mainKmat, CarriageCstic.StandardKits)[0] === "N",
                  value: false,
                },
                true,
              ],
            },
            [CsticNames.LubricationConnectorAlignment]: {
              name: CarriageCstic.LubricationConnectorAlignment,
              required: true,
              visible: [
                // {
                //   condition: ({ mainKmat }) => {
                //     const [lubricationConnectorStatus] = getCsticsValue(
                //       mainKmat,
                //       CarriageCstic.LubricationConnectorOrientation,
                //     );
                //     return ["L", "R"].includes(lubricationConnectorStatus);
                //   },
                //   value: true,
                // },
                false,
              ],
            },
          },

          helperCstics: {
            [CsticNames.StandardKits]: {
              name: CarriageCstic.StandardKits,
            },
            [CsticNames.LubricationConnectorType]: {
              name: CarriageCstic.LubricationConnectorType,
            },
            [CsticNames.EnableCarriageEditButton]: {
              name: CarriageCstic.CarriageDesign,
            },
          },

          csticsToReset: [
            CarriageCstic.StandardKits,
            CarriageCstic.KitsLeft,
            CarriageCstic.KitsRight,
            CarriageCstic.KitsCenter,
            CarriageCstic.LubricationConnectorPosition,
            CarriageCstic.LubricationConnector,
            CarriageCstic.LubricationConnectorOrientation,
            CarriageCstic.LubricationConnectorAlignment,
          ],
        },
      ],
    },
    {
      name: "configurator.carriage.page.3.name",
      enabled: true,
      resetLabel: "",
      resetRequiresConfirmation: false,
      substeps: [
        {
          type: SubstepType.SummaryPage,
          visible: true,

          info: [
            {
              condition: ({ mainKmat }) => {
                return userIsCustomer();
              },
              value: "configurator.carriage.summary.info.above.business",
            },
            {
              condition: ({ mainKmat }) => {
                return true;
              },
              value: "configurator.carriage.summary.info.above.plus",
            },
          ],
        },
      ],
    },
  ],
};
